import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle, XCircle, Clock } from "lucide-react";
import { formatDate } from "@/lib/dateUtils";
import { CuteLoader } from "@/components/CuteLoader";

export default function HealthReimbursement() {
  const { user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);

  const loadSubmission = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    try {
      const { data } = await api.get("/employee/health-reimbursement", { signal: controller.signal });
      setSubmission(data);
      setError(null);
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('Request timed out');
      } else if (error.response?.status !== 404) {
        setError('Could not load submission');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubmission();
  }, [loadSubmission]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (selectedFile.size > maxSize) {
      toast.error("File too large. Maximum 10MB.");
      return;
    }
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Only JPG, PNG, and PDF files allowed");
      return;
    }
    
    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post("/employee/health-reimbursement/upload", formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success("Proof of coverage submitted successfully");
      setFile(null);
      loadSubmission();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload proof");
    } finally {
      setUploading(false);
    }
  };

  const getStatusDisplay = () => {
    if (!submission) {
      return {
        icon: <Upload className="w-12 h-12 text-[#9C6B4A]" />,
        label: "Not Submitted",
        description: "Upload proof of your coverage to enroll — it only takes a minute, and the reimbursement starts from there.",
        color: "text-[#9C6B4A]"
      };
    }
    
    switch (submission.status) {
      case 'under_review':
        return {
          icon: <Clock className="w-12 h-12 text-[#B0905E]" />,
          label: "Under Review",
          description: "Your submission is being reviewed",
          color: "text-[#B0905E]"
        };
      case 'approved':
        return {
          icon: <CheckCircle className="w-12 h-12 text-green-600" />,
          label: "Approved",
          description: `Effective ${formatDate(submission.effective_date)}`,
          color: "text-green-600"
        };
      case 'declined':
        return {
          icon: <XCircle className="w-12 h-12 text-red-600" />,
          label: "Needs Update",
          description: submission.decline_reason || "Please submit updated proof",
          color: "text-red-600"
        };
      case 'needs_renewal':
        return {
          icon: <Clock className="w-12 h-12 text-[#9C6B4A]" />,
          label: "Needs Renewal",
          description: "Your coverage proof has expired. Please submit updated proof.",
          color: "text-[#9C6B4A]"
        };
      default:
        return {
          icon: <Upload className="w-12 h-12 text-[#9C6B4A]" />,
          label: "Unknown Status",
          description: "",
          color: "text-[#9C6B4A]"
        };
    }
  };

  if (loading) {
    return <CuteLoader />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-[#9C6B4A] text-lg mb-4 font-serif">{error}</div>
          <button
            onClick={() => { setLoading(true); setError(null); loadSubmission(); }}
            className="px-6 py-3 bg-[#9C6B4A] text-white rounded-lg hover:bg-[#8B5A39] transition font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const status = getStatusDisplay();
  const canUpload = !submission || submission.status === 'declined' || submission.status === 'needs_renewal';

  return (
    <div className="min-h-screen bg-[#FAF7F1] p-6 pb-24">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-serif text-[#231E1A] mb-2">Health Reimbursement</h1>
        <p className="text-sm text-[#9C6B4A] mb-6">GT reimburses part of your health insurance — upload proof of your coverage to enroll</p>
        
        <div className="bg-white rounded-lg p-8 mb-4 border border-[#EAE1D4] text-center">
          <div className="flex justify-center mb-4">
            {status.icon}
          </div>
          <h2 className={`text-xl font-semibold mb-2 ${status.color}`}>{status.label}</h2>
          <p className="text-[#3A2A1E]">{status.description}</p>
        </div>

        {submission?.submitted_at && (
          <div className="bg-white rounded-lg p-6 mb-4 border border-[#EAE1D4]">
            <h3 className="text-sm font-semibold text-[#231E1A] mb-3">Submission Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#9C6B4A]">Submitted</span>
                <span className="text-[#231E1A]">
                  {formatDate(submission.submitted_at)}
                </span>
              </div>
              {submission.reviewed_at && (
                <div className="flex justify-between">
                  <span className="text-[#9C6B4A]">Reviewed</span>
                  <span className="text-[#231E1A]">
                    {formatDate(submission.reviewed_at)}
                  </span>
                </div>
              )}
              {submission.expiration_date && submission.status === 'approved' && (
                <div className="flex justify-between">
                  <span className="text-[#9C6B4A]">Renewal Due</span>
                  <span className="text-[#231E1A]">
                    {formatDate(submission.expiration_date)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {canUpload && (
          <div className="bg-white rounded-lg p-6 border border-[#EAE1D4]">
            <h3 className="text-sm font-semibold text-[#231E1A] mb-4">
              {submission ? "Upload Updated Proof" : "Upload Proof of Coverage"}
            </h3>
            <p className="text-sm text-[#9C6B4A] mb-4">
              Submit a photo of your insurance card or a coverage letter (JPG, PNG, or PDF, max 10MB)
            </p>
            
            <div className="mb-4">
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="flex items-center justify-center gap-2 w-full py-3 px-4 border-2 border-dashed border-[#EAE1D4] rounded-lg cursor-pointer hover:border-[#9C6B4A] transition"
              >
                <FileText className="w-5 h-5 text-[#9C6B4A]" />
                <span className="text-[#231E1A]">{file ? file.name : "Choose File"}</span>
              </label>
            </div>

            {file && (
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="w-full bg-[#9C6B4A] text-white py-3 rounded-lg hover:bg-[#8B5A39] transition disabled:opacity-50 font-medium"
              >
                {uploading ? "Uploading..." : "Submit Proof"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
